import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReqOktaLambdaParams, UrlParams } from "../../../common/interfaces/impersonation";
import "./JWTReceiver.scss";
import { Config } from "../../../common/interfaces/config";
import { fetchConfig } from "../../../common/util/config";
import { SetBootForCurrentBusiness, SetLoginFields, initAccBoot } from "../../../reducers/model/boot";
import { BootUser, setIsImpersonation } from "../../../reducers/boot";
import { LoginFields, setLocalStorageItem } from "../../../reducers/loginLocalStorage";
import { persistor, store } from "../../../store";
import {
  useLazyGetMyBizShellConfigQuery,
  useLazyGetBusinessSelectorQuery,
} from "../../../services/MyBizShell/mybizShellAPI";
import { fetchTokenValidation, type TokenValidationResponse } from "../../../services/Authentication/tokenValidation";
import { deleteCookies } from "../../../common/util/cookies";
import {
  getLambdaEnvironment,
  loadAmwayIdAuthConfigs,
  loadOktaAuthConfigs,
  parseJwt,
  redirectAccountFailed,
  redirectAuthFailed,
  redirectBusinessSelector,
} from "../impersonation-util";
import {
  checkForAuthError,
  checkWebsiteUnavailable,
  setHomePage,
  setlocalNSessionStorage,
  logRedactedHash,
  getLoginFields,
  deleteCache,
  deleteRequestCache,
} from "../Token";
import { sessionLog } from "../util";

type Business = {
  isoCountryCode: string;
  abo: string;
  affiliate: string;
};

/**
 * Determines the ISO country code with fallback logic:
 * 1. Try to get from token's account.country field
 * 2. Fall back to ssoOrigin from URL parameters
 */
const determineIsoCountryCode = (tokenCountry: string | undefined): string | undefined => {
  if (tokenCountry) {
    return tokenCountry;
  }

  const localStorageFields = store.getState().loginLocalStorage.loginFields;
  return localStorageFields.ssoOrigin;
};

export const JwtReceiver = () => {
  const navigate = useNavigate();
  const [getShellConfig] = useLazyGetMyBizShellConfigQuery();
  const [getBusinessSelector] = useLazyGetBusinessSelectorQuery();
  const authInit = useRef(false);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const setProgress = (percent: string) => {
    if (progressBarRef.current) {
      progressBarRef.current.style.width = percent;
    }
  };

  useEffect(() => {
    if (authInit.current) return;
    authInit.current = true;

    const handleJWTAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionToken = store.getState()?.boot?.user?.access_token;
      if (params.has("token") && sessionToken && params.get("token") !== sessionToken) {
        sessionStorage.clear();
        deleteCache();
        deleteRequestCache();
        deleteCookies();
        // allow the browser some time to clear things before navigating
        // prevents getting stuck in a loop
        setTimeout(() => window.location.replace(window.location.href), 100);
      }

      logRedactedHash();
      setProgress("20%");

      // Read login fields from URL params BEFORE fetchConfig so the correct
      // ssoOrigin is in the store when regionalConfigPath() is called.
      const localStorageFields: LoginFields = getLoginFields();
      SetLoginFields({ fields: localStorageFields });

      if (localStorageFields.ssoOrigin) {
        SetLoginFields({ fields: { ssoOrigin: localStorageFields.ssoOrigin! } });
      }

      const defaultConfig: any = await fetchConfig();

      if (defaultConfig?.env) {
        checkWebsiteUnavailable(defaultConfig, navigate);
        setProgress("40%");
        checkForAuthError(navigate);
        setlocalNSessionStorage(defaultConfig, localStorageFields, navigate);

        if (params.has("token")) {
          await tokenDefinedAuthFlow(params, defaultConfig, localStorageFields);
        } else {
          const state = params.get("state");
          const decodedState = JSON.parse(atob(state as string));
          let config: any;
          // the AmwayID flow must check the uid from session storage against the returned state from AmwayID
          if (
            window.sessionStorage.getItem("uid") !== null &&
            decodedState.uid !== null &&
            decodedState.uid !== undefined
          ) {
            // if there is a uid in session storage, load the AmwayID auth configs
            config = loadAmwayIdAuthConfigs(defaultConfig);
            if (window.sessionStorage.getItem("uid") !== decodedState.uid) {
              redirectAuthFailed(navigate);
            }
          } else {
            // otherwise, load the Okta auth configs for impersonation
            config = loadOktaAuthConfigs(defaultConfig);
          }
          // generate the requestOktaTokens lambda
          const requestOktaTokensLambdaUrl = generateRequestOktaLambdaUrl(
            {
              environment: defaultConfig.env,
              lambdaEnvironment: getLambdaEnvironment(defaultConfig.env),
              authCode: params.get("code")!,
              config,
            },
            navigate,
          );

          // declare the data fetching function
          const fetchJWTToken = async (navigate: Function) => {
            await fetch(requestOktaTokensLambdaUrl)
              .then((response) => {
                if (!response.ok) {
                  console.error(`requestOktaTokens failed with status ${response.status}`);
                  throw new Error(`requestOktaTokens returned ${response.status}`);
                }
                return response.json();
              })
              .then(async (data) => {
                if (!data.access_token) {
                  console.error("requestOktaTokens response is missing 'access_token'.", data);
                  throw new Error("Missing access_token in Okta token response");
                }
                const isImpersonation = data?.isImpersonation === true;
                // The Okta exchange is authoritative. Set Redux before any boot-dependent
                // initialization or routing can observe the stale login-origin fallback.
                store.dispatch(setIsImpersonation(isImpersonation));
                SetLoginFields({ fields: { isImpersonation } });
                setProgress("60%");
                const parsedAccessToken = parseJwt(data.access_token);

                if (!parsedAccessToken.account) {
                  console.error(
                    "JWT token is missing the 'account' object. The token must include account.abo, account.sales_plan_aff, and account.lcl_partyid.",
                  );
                  redirectAuthFailed(navigate);
                  return;
                }

                const isoCountryCode = determineIsoCountryCode(parsedAccessToken.account.country) || "";

                const user: BootUser = {
                  token_type: data.token_type,
                  access_token: data.access_token,
                  expires_in: data.expires_in,
                  abo: parsedAccessToken.account.abo,
                  aff: parsedAccessToken.account.sales_plan_aff,
                  partyId: parsedAccessToken.account.lcl_partyid,
                  globalPartyId: parsedAccessToken.gbl_partyid,
                  idToken: data.id_token,
                  isoCountryCode: isoCountryCode,
                  cacheDateInMS: new Date().getTime(),
                };

                const updatedBoot = await SetBootForCurrentBusiness({ user, isImpersonation });
                await initAccBoot(updatedBoot);
                await persistor.flush();
              })
              .then(async () => {
                const { isImpersonation } = store.getState().boot;

                // checking if the idtoken has accounts and redirecting based on the status of accounts if there is more then one active account
                const parsedIdToken = parseJwt(await store.getState().boot.user.idToken);
                const { account, accounts: businessAccounts, origin } = parsedIdToken;

                let activeAccounts: any;
                let selectedAccount: any;
                let useGluuAuth = false;
                const configuration = store.getState().boot.configuration;

                if (configuration && configuration.useGluuAuth) {
                  useGluuAuth = configuration.useGluuAuth;
                }

                if (useGluuAuth && Array.isArray(account)) {
                  activeAccounts = account.filter((account: any) => account.status.toUpperCase() === "ACTIVE");
                  if (activeAccounts[0]) {
                    selectedAccount = activeAccounts[0];
                  }
                } else {
                  activeAccounts = businessAccounts.filter((account: any) => account.status.toUpperCase() === "ACTIVE");
                  // account is an object
                  if (account && !Array.isArray(account) && account.status.toUpperCase() === "ACTIVE") {
                    selectedAccount = account;
                  }
                }

                const businessList: Business[] = [];
                const scopeAff = decodedState.sales_plan_aff || decodedState.salesPlanAff;
                const scopeAbo = decodedState.abo || decodedState.aboNumber;
                if (
                  origin &&
                  origin.toUpperCase() === "IMPORTED" &&
                  scopeAff &&
                  scopeAbo &&
                  !isImpersonation &&
                  (selectedAccount === undefined ||
                    scopeAff !== selectedAccount.sales_plan_aff ||
                    scopeAbo !== selectedAccount.abo)
                ) {
                  redirectAccountFailed(navigate);
                  return;
                }
                if (!defaultConfig.hideAccountSelector) {
                  if (
                    (scopeAff === selectedAccount.sales_plan_aff && scopeAbo === selectedAccount.abo) ||
                    activeAccounts.length === 1 ||
                    (!activeAccounts.length && selectedAccount.status.toUpperCase() === "ACTIVE") ||
                    isImpersonation
                  ) {
                    await setHomePage(navigate, getShellConfig, getBusinessSelector);
                  } else if (activeAccounts.length > 1) {
                    activeAccounts.forEach((account: any) =>
                      businessList.push({
                        abo: account.abo,
                        isoCountryCode: account.country,
                        affiliate: account.sales_plan_aff,
                      }),
                    );

                    await redirectBusinessSelector(businessList, navigate);
                    return;
                  } else {
                    sessionLog(`There are no active accounts.`);
                    redirectAuthFailed(navigate);
                    return;
                  }
                } else {
                  await setHomePage(navigate, getShellConfig, getBusinessSelector);
                }
              })
              .catch((error) => {
                console.error(error);
                redirectAuthFailed(navigate);
                return;
              });
          };
          // call the function
          fetchJWTToken(navigate);
        }
      }
    };

    handleJWTAuth();
  }, []);

  const initBootWithPreMintedToken = async (_token: string, isImpersonation: boolean) => {
    const parsedAccessToken = parseJwt(_token);

    if (!parsedAccessToken.account) {
      console.error(
        "JWT token is missing the 'account' object. The token must include account.abo, account.sales_plan_aff, and account.lcl_partyid.",
      );
      redirectAuthFailed(navigate);
      return;
    }

    const isoCountryCode = determineIsoCountryCode(parsedAccessToken.account.country) || "";

    // set the user and tokens objects to be used in init() and setState()
    const user: BootUser = {
      token_type: _token.token_type || parsedAccessToken.token_type || "bearer",
      access_token: _token,
      expires_in: parsedAccessToken.exp,
      abo: parsedAccessToken.account.abo,
      aff: parsedAccessToken.account.sales_plan_aff,
      partyId: parsedAccessToken.account.lcl_partyid,
      globalPartyId: parsedAccessToken.gbl_partyid,
      idToken: _token.id_token,
      isoCountryCode: isoCountryCode,
      cacheDateInMS: new Date().getTime(),
    };

    const updatedBoot = await SetBootForCurrentBusiness({ user, isImpersonation });
    await initAccBoot(updatedBoot);
    await persistor.flush();
  };

  const validateTokenImpersonation = async (token: string, config: Config): Promise<TokenValidationResponse> => {
    try {
      return await fetchTokenValidation(token, config);
    } catch (error) {
      sessionLog(error instanceof Error ? error.message : "Token validation failed.");
      throw error;
    }
  };

  const handleJwtFromUrlParam = async (params: URLSearchParams, config: Config) => {
    setProgress("50%");
    const token = params.get("token");
    if (!token) {
      throw new Error("Missing pre-minted token");
    }

    store.dispatch(setLocalStorageItem({ clientId: config.apiClientId }));
    const tokenValidationResponse = await validateTokenImpersonation(token, config);
    const isImpersonation = tokenValidationResponse.isImpersonation === true;
    store.dispatch(setIsImpersonation(isImpersonation));
    SetLoginFields({ fields: { isImpersonation } });
    await initBootWithPreMintedToken(token, isImpersonation);

    // Log in!
    sessionLog("Successful login!");
    // fetch coreplus-shell api as data is needed for Monthly and Annual Dashboard to load properly
    // fetch business selector data as LOS and AR are required it to load properly
    await setHomePage(navigate, getShellConfig, getBusinessSelector);
  };

  const tokenDefinedAuthFlow = async (params: URLSearchParams, config: Config, localStorageFields: LoginFields) => {
    // if token is defined, we assume it's trying
    // to use a different auth flow
    if (
      localStorageFields.ssoOrigin == null ||
      localStorageFields.entryPage == null ||
      localStorageFields.locale == null
    ) {
      redirectAuthFailed(navigate);
      return;
    }

    await handleJwtFromUrlParam(params, config);
    return;
  };

  const generateRequestOktaLambdaUrl = (params: ReqOktaLambdaParams, navigate: Function) => {
    //get the configs for the lambda URL
    const {
      AuthServer: authServer,
      ClientId: clientId,
      IdpDomain: idpDomain,
      OktaTokenApiUrlBase: oktaTokenApiUrlBase,
      UseNewOktaTokenApi,
      UseGluuAuth,
      GluuTokenUrl,
    } = params.config;

    const local = window.location.href.startsWith("http://localhost");
    const localJwtTokenReceiverUrl = "http://localhost:8000/jwt-receiver.html";

    const redirectUri = local ? localJwtTokenReceiverUrl : params.config.RedirectUri;

    // format the requestOktaTokens lambda URL
    const firstPartUrl = (env: string, lambdaEnv: string) =>
      params.environment === "prod"
        ? `https://acc-api-global.gmb-${lambdaEnv}`
        : `https://acc-api-global-${env}.gmb-${lambdaEnv}`;

    const formatFullImpersonationLambdaUrl = (urlVars: UrlParams) => {
      const legacyOktaTokenUrlBase = `${firstPartUrl(
        params.environment,
        params.lambdaEnvironment,
      )}.corp.amway.net/api/requestOktaTokens?clientId=${urlVars.clientId}&code=${urlVars.authCode}&oktaRedirectUri=${
        urlVars.redirectUri
      }`;

      const oktaTokenUrlBase =
        UseNewOktaTokenApi && oktaTokenApiUrlBase
          ? `${oktaTokenApiUrlBase}/requestOktaTokens?clientId=${urlVars.clientId}&code=${urlVars.authCode}&oktaRedirectUri=${urlVars.redirectUri}`
          : legacyOktaTokenUrlBase;

      if (urlVars.useGluuAuth) {
        return `${oktaTokenUrlBase}&gluuTokenUrl=${urlVars.gluuTokenUrl}`;
      }

      return `${oktaTokenUrlBase}&oktaAuthServer=${urlVars.authServer}&idpDomain=${urlVars.idpDomain}`;
    };

    const requestOktaTokensLambdaUrl = formatFullImpersonationLambdaUrl({
      clientId,
      authCode: params.authCode!,
      authServer,
      redirectUri,
      idpDomain,
      useGluuAuth: UseGluuAuth || false,
      gluuTokenUrl: GluuTokenUrl || "",
    });

    return requestOktaTokensLambdaUrl;
  };

  return (
    <div className="JwtImpersonation__progress">
      <div className="JwtImpersonation__bar" ref={progressBarRef}></div>
    </div>
  );
};
